import { Selector, createSelector } from 'reselect'

import {
  idParam,
} from './router'

import {
  networkGroup,
} from './utils'

import {
  IRecord_KnowledgeBase,
  IMeta_ProjectConfig_App_Config_Knowledgebase,
  IData_KnowledgeBase_Section_Summary,
  IMeta_ProjectConfig_App_Config_Knowledgebase_Type,
  IRecord_KnowledgeBase_Summary,
  IRecord_KnowledgeBase_Page,
} from '@kaidam/fc-shared/src/types/schema/knowledgebase'

import {
  IElasticSearchResult,
} from '@kaidam/fc-shared/src/types/elasticsearch'

import {
  IElasticSearch_Source_Knowledgebase,
} from '@kaidam/fc-shared/src/types/knowledgebase'

import {
  IMeta_ProjectConfig_App_Config,
} from '@kaidam/fc-shared/src/types/schema/project_config'

import {
  sortArticles,
  getDefaultSortSettings,
  getDefaultSettings,
} from '@kaidam/fc-shared/src/utils/knowledgebase'

import {
  getSummaryNamespaceRoles,
  hasRoleAccessToType,
} from '@kaidam/fc-shared/src/utils/role'

import {
  RootState,
} from '../../types/store'

import projectSelectors from './project'
import routerSelectors from './router'

export const data: Selector<RootState, IRecord_KnowledgeBase[]> = state => 
  state.knowledgebase.data

export const article: Selector<RootState, IRecord_KnowledgeBase> = state => 
  state.knowledgebase.article

export const summary: Selector<RootState, IData_KnowledgeBase_Section_Summary[]> = state => 
  state.knowledgebase.summary

export const allData: Selector<RootState, Record<string, IRecord_KnowledgeBase[]>> = state => 
  state.knowledgebase.allData

export const searchResults: Selector<RootState, IElasticSearchResult<IElasticSearch_Source_Knowledgebase>[]> = state =>
  state.knowledgebase.searchResults

export const editItem = createSelector(
  data,
  idParam,
  (data, routeId) => data.find(form => form.id == routeId)
)

// return the 'types' from the current project knowledgebase config
// we look at the users role and filter down the allowed types from there
export const typeConfigs = createSelector(
  projectSelectors.summary,
  projectSelectors.appConfig,
  (summary, configs) => {
    const roles = getSummaryNamespaceRoles(summary, 'knowledgebase')
    if(roles.length <= 0) return []
    const rawConfig: IMeta_ProjectConfig_App_Config | undefined = configs.find(c => c.namespace == 'knowledgebase')
    if(!rawConfig) return []
    const appConfig = rawConfig.config as IMeta_ProjectConfig_App_Config_Knowledgebase
    const types = appConfig.types || []
    return types.filter(type => {
      return roles.filter(role => hasRoleAccessToType(role, type.name)) ? true : false
    })
  }
)

// pluck the 'type' param from the router as the current filter
export const currentPageType = createSelector(
  routerSelectors.params,
  (params) => params.type as string,
)

export const typeConfig = createSelector(
  typeConfigs,
  currentPageType,
  (configs, type) => {
    return configs.find(c => c.name == type)
  }
)

export const settings = createSelector(
  typeConfig,
  (config) => getDefaultSettings(config.settings)
)

export const sortSettings = createSelector(
  settings,
  (settings) => getDefaultSortSettings(settings.sorting)
)

export const sortedData = createSelector(
  data,
  sortSettings,
  (data, sortSettings) => {
    return sortArticles(data, sortSettings)
  }
)

export const summaryItems = createSelector(
  summary,
  (summary) => {
    const sections: Record<string, IMeta_ProjectConfig_App_Config_Knowledgebase_Type> = {}
    const sectionArticles: Record<string, IRecord_KnowledgeBase_Summary[]> = {}
    const articles: Record<string, IRecord_KnowledgeBase_Summary> = {}
    const pages: Record<string, IRecord_KnowledgeBase_Page> = {}

    summary.forEach(s => {
      sections[s.section.name] = s.section
      sectionArticles[s.section.name] = s.articles
      s.articles.forEach((article) => {
        articles[article.id] = article
        const usePages = (article.meta || {}).pages || []
        usePages.forEach(p => {
          pages[p.id] = p
        })
      })
    })

    return {
      sections,
      sectionArticles,
      articles,
      pages,
    }
  }
)

export const network = networkGroup('knowledgebase', [
  'list',
  'search',
  'create',
  'update',
  'delete',
  'loadSummary',
])

export const formLoading = createSelector(
  network.create.loading,
  network.update.loading,
  (create, update) => create || update,
)

export const formError = createSelector(
  network.create.error,
  network.update.error,
  (create, update) => create || update,
)

export default {
  data,
  article,
  summary,
  summaryItems,
  allData,
  searchResults,
  editItem,
  typeConfigs,
  currentPageType,
  typeConfig,
  settings,
  sortSettings,
  sortedData,
  formLoading,
  formError,
  network,
}
